import type { LeadSide, ServiceLine } from "./schemas.js";

/**
 * Every place we go looking for leads.
 *
 * Shared so the admin's picker and the worker agree on what a source is called,
 * and so adding one is a single entry here rather than a change in three files.
 *
 * Nothing on this list runs on a schedule. Searching is something a person does
 * when they want work, not something that happens nightly whether or not anyone
 * is going to read the result -- and for the sources that read someone else's
 * site, a crawl nobody asked for is discourtesy for no return.
 */

export interface LeadSource {
  slug: string;
  label: string;
  side: LeadSide;
  /** Shown under the checkbox, so the choice is informed. */
  hint: string;
  /** True when the source searches the region list rather than its own config. */
  usesRegions?: boolean;
  /**
   * Which line of the business the leads belong to. Absent means cleaning,
   * which is what every source found before property management existed.
   */
  serviceLine?: ServiceLine;
}

export const leadSources: readonly LeadSource[] = [
  {
    slug: "kijiji",
    label: "Kijiji classifieds",
    side: "supply",
    hint: "Individual cleaners advertising locally. Slow — a few seconds per ad, a minute or two per run.",
    usesRegions: true,
  },
  {
    slug: "places",
    label: "Cleaning companies (Google)",
    side: "supply",
    hint: "Established cleaning businesses in the area. Needs the Places API key.",
    usesRegions: true,
  },
  {
    slug: "places-demand",
    label: "Businesses that need cleaning (Google)",
    side: "demand",
    hint: "Offices, clinics, gyms, warehouses and the like across the service area. Capped per run to bound the API spend.",
  },
  {
    slug: "kijiji-rentals",
    label: "Rental listings (Kijiji)",
    side: "demand",
    serviceLine: "property-management",
    hint: "Whoever advertises a unit owns or manages it. Mixed quality on purpose — a big complex is already managed, an owner with two doors is the one worth calling. Slow, like the cleaner search.",
    usesRegions: true,
  },
  {
    slug: "tenders",
    label: "Public cleaning contracts",
    side: "demand",
    hint: "Janitorial and facility contracts out for tender, from CanadaBuys open data. Federal only, so a handful at a time.",
  },
  {
    slug: "tenders-property",
    label: "Public property management contracts",
    side: "demand",
    serviceLine: "property-management",
    hint: "Building and facility management put out to tender. Rare — CanadaBuys is federal only, so expect one or two, not a list.",
  },
  {
    slug: "permits",
    label: "Building permits (Barrie)",
    side: "demand",
    hint: "New builds and fit-outs that will need a post-construction clean, from Barrie's open data.",
  },
] as const;

export const leadSourceSlugs = leadSources.map((s) => s.slug);

export function leadSourcesForSide(side: LeadSide): readonly LeadSource[] {
  return leadSources.filter((s) => s.side === side);
}

/** Kept for the admin's supply-side picker. */
export const supplySources = leadSourcesForSide("supply");
export const supplySourceSlugs = supplySources.map((s) => s.slug);
